/**
 * Skill manifest loader — loads skills from SKILL.md files and backend API.
 *
 * SKILL.md format:
 * ```
 * ---
 * name: code-review
 * version: "1.0.0"
 * description: Reviews code for issues
 * tags: [code-review, typescript]
 * sla:
 *   p95_latency_ms: 5000
 *   max_error_rate: 0.05
 * schema:
 *   type: object
 *   properties:
 *     code:
 *       type: string
 *       description: Code to review
 *   required: [code]
 * ---
 * # Code Review Skill
 *
 * Instructions for invoking this skill...
 * ```
 */
import { readFileSync, existsSync, readdirSync } from "node:fs";
import { resolve, join } from "node:path";
import type { SkillDefinition } from "./mcp-server.js";

export interface SkillManifest {
  skills: SkillDefinition[];
  source: "local" | "remote" | "merged";
}

interface AgentVaultNamespace {
  certification?: string;           // "verified" | "certified" | "enterprise"
  integrity?: {
    algorithm?: string;             // "XChaCha20-Poly1305"
    hashChain?: string;             // "SHA-256"
  };
  requiredPolicies?: string[];      // ["network: agentvault"]
  runtime?: {
    capabilities?: string[];
    forbidden?: string[];
    output_schema?: Record<string, unknown>;
  };
  model?: {
    allowed?: string[];
    default?: string;
    routing?: string;
  };
}

interface SkillFrontmatter {
  name: string;
  version?: string;
  description?: string;
  tags?: string[];
  sla?: {
    p95_latency_ms?: number;
    max_error_rate?: number;
    min_uptime_pct?: number;
    max_avg_tokens?: number;
  };
  schema?: Record<string, unknown>;
  agentVault?: AgentVaultNamespace;
}

/**
 * Parse a SKILL.md file into a SkillDefinition.
 *
 * Extracts YAML frontmatter between --- delimiters.
 * Everything after the closing --- is treated as instructions.
 */
export function parseSkillMd(content: string): SkillDefinition | null {
  const lines = content.split("\n");

  // Find frontmatter delimiters
  if (lines[0]?.trim() !== "---") return null;

  let endIdx = -1;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i]?.trim() === "---") {
      endIdx = i;
      break;
    }
  }

  if (endIdx === -1) return null;

  // Parse YAML frontmatter (simple key-value parser)
  const frontmatterLines = lines.slice(1, endIdx);
  const frontmatter = parseSimpleYaml(frontmatterLines.join("\n")) as unknown as SkillFrontmatter;

  if (!frontmatter.name) return null;

  // Everything after frontmatter is instructions
  const instructionLines = lines.slice(endIdx + 1);
  const instructions = instructionLines.join("\n").trim();

  const skill: SkillDefinition = {
    name: frontmatter.name as string,
    version: frontmatter.version as string | undefined,
    description: frontmatter.description as string | undefined,
    tags: frontmatter.tags,
    inputSchema: frontmatter.schema as Record<string, unknown> | undefined,
    slaDefinition: frontmatter.sla as Record<string, unknown> | undefined,
    instructions: instructions || undefined,
  };

  // Map agentVault namespace to extended SkillDefinition fields
  if (frontmatter.agentVault) {
    const av = frontmatter.agentVault;
    if (av.certification) skill.certificationTier = av.certification;
    if (av.runtime?.capabilities) skill.toolsAllowed = av.runtime.capabilities;
    if (av.runtime?.forbidden) skill.toolsDenied = av.runtime.forbidden;
    if (av.runtime?.output_schema) skill.outputSchema = av.runtime.output_schema;
    if (av.model?.routing) skill.modelRouting = av.model.routing;
    if (av.model?.allowed) skill.allowedModels = av.model.allowed;
    if (av.model?.default) skill.defaultModel = av.model.default;
    if (av.integrity) skill.integrity = av.integrity as Record<string, unknown>;
    if (av.requiredPolicies) skill.requiredPolicies = av.requiredPolicies;
  }

  return skill;
}

/**
 * Simple YAML parser for frontmatter (handles basic key-value, arrays, nested objects).
 * Supports up to 3 levels of nesting (needed for agentVault namespace).
 * Not a full YAML parser — covers the subset used in SKILL.md files.
 */
function parseSimpleYaml(yaml: string): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const lines = yaml.split("\n");

  // Stack-based parser to support multi-level nesting
  const stack: Array<{ key: string; obj: Record<string, unknown>; indent: number }> = [];
  let currentObj = result;

  function parseValue(raw: string): unknown {
    const value = raw.replace(/^["']|["']$/g, "");
    const num = Number(value);
    if (!isNaN(num) && value !== "") return num;
    if (value === "true") return true;
    if (value === "false") return false;
    return value;
  }

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) continue;

    const indent = line.length - line.trimStart().length;

    // Pop stack back to appropriate level
    while (stack.length > 0 && indent <= stack[stack.length - 1]!.indent) {
      const popped = stack.pop()!;
      currentObj = stack.length > 0 ? stack[stack.length - 1]!.obj : result;
      // Attach the completed nested object to its parent
      currentObj[popped.key] = popped.obj;
    }

    // Inline array: key: [val1, val2]
    const inlineArrayMatch = trimmed.match(/^(\w[\w_-]*)\s*:\s*\[(.+)\]$/);
    if (inlineArrayMatch) {
      const key = inlineArrayMatch[1]!;
      const values = inlineArrayMatch[2]!
        .split(",")
        .map((v) => v.trim().replace(/^["']|["']$/g, ""));
      if (stack.length > 0) {
        stack[stack.length - 1]!.obj[key] = values;
      } else {
        currentObj[key] = values;
      }
      continue;
    }

    // Key with value: key: value
    const kvMatch = trimmed.match(/^(\w[\w_-]*)\s*:\s*(.+)$/);
    if (kvMatch) {
      const key = kvMatch[1]!;
      const val = parseValue(kvMatch[2]!);
      if (stack.length > 0) {
        stack[stack.length - 1]!.obj[key] = val;
      } else {
        currentObj[key] = val;
      }
      continue;
    }

    // Nested object start (key with no value — "key:")
    const nestedMatch = trimmed.match(/^(\w[\w_-]*)\s*:$/);
    if (nestedMatch) {
      const key = nestedMatch[1]!;
      const newObj: Record<string, unknown> = {};
      stack.push({ key, obj: newObj, indent });
      continue;
    }
  }

  // Flush remaining stack
  while (stack.length > 0) {
    const popped = stack.pop()!;
    const parent = stack.length > 0 ? stack[stack.length - 1]!.obj : result;
    parent[popped.key] = popped.obj;
  }

  return result;
}

/**
 * Load skills from a directory by scanning for SKILL.md files.
 *
 * Matches:
 * - SKILL.md (exact match)
 * - *-SKILL.md or *_SKILL.md (e.g., Cortina-SKILL.md)
 * - Subdirectories containing SKILL.md (one level deep)
 */
export function loadSkillsFromDirectory(dir: string): SkillDefinition[] {
  const skills: SkillDefinition[] = [];
  const absDir = resolve(dir);

  if (!existsSync(absDir)) return skills;

  const seen = new Set<string>();

  function tryLoad(filePath: string): void {
    if (seen.has(filePath)) return;
    seen.add(filePath);
    try {
      const content = readFileSync(filePath, "utf-8");
      const skill = parseSkillMd(content);
      if (skill) skills.push(skill);
    } catch {
      // File not readable, skip
    }
  }

  try {
    const entries = readdirSync(absDir, { withFileTypes: true });
    for (const entry of entries) {
      if (entry.isFile() && entry.name.endsWith("SKILL.md")) {
        // Match SKILL.md, Cortina-SKILL.md, my_SKILL.md, etc.
        tryLoad(join(absDir, entry.name));
      } else if (entry.isDirectory()) {
        // Check for SKILL.md inside subdirectories (one level deep)
        const subSkill = join(absDir, entry.name, "SKILL.md");
        if (existsSync(subSkill)) {
          tryLoad(subSkill);
        }
      }
    }
  } catch {
    // Directory not readable, skip
  }

  return skills;
}

/**
 * Load skills from backend API.
 */
export async function loadSkillsFromApi(
  apiUrl: string,
  apiKey: string,
  hubId: string,
): Promise<SkillDefinition[]> {
  try {
    const res = await fetch(`${apiUrl}/api/v1/hub/identities/${hubId}/skills`, {
      headers: { Authorization: `Bearer ${apiKey}` },
    });
    if (!res.ok) return [];

    const data = (await res.json()) as Array<{
      capability_name: string;
      capability_version?: string;
      description?: string;
      skill_content?: string;
      mcp_metadata?: Record<string, unknown>;
      schema_definition?: Record<string, unknown>;
      sla_definition?: Record<string, unknown>;
      tags?: string[];
      instructions?: string;
    }>;

    return data.map((cap) => ({
      name: cap.capability_name,
      version: cap.capability_version,
      description: cap.description,
      inputSchema: cap.schema_definition,
      slaDefinition: cap.sla_definition,
      tags: cap.tags,
      instructions: cap.instructions,
    }));
  } catch {
    return [];
  }
}

/**
 * Merge local and remote skills. Local definitions take precedence.
 */
export function mergeSkills(
  local: SkillDefinition[],
  remote: SkillDefinition[],
): SkillManifest {
  const byName = new Map<string, SkillDefinition>();

  // Remote first (lower precedence)
  for (const skill of remote) {
    byName.set(skill.name, skill);
  }

  // Local overwrites (higher precedence)
  for (const skill of local) {
    byName.set(skill.name, skill);
  }

  const source =
    local.length > 0 && remote.length > 0
      ? "merged"
      : local.length > 0
        ? "local"
        : "remote";

  return {
    skills: Array.from(byName.values()),
    source,
  };
}
